import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import SearchableSelect from '../../components/common/SearchableSelect';

const emptyItemForm = { name: '', sku: '', unit_id: '', opening_qty: 0, is_active: true };

export default function InventoryItems() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, search: '', per_page: 100 });
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [itemModal, setItemModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.inventory.items(params);
      const list = Array.isArray(data.data) ? data.data : data.items || data;
      setRows(Array.isArray(list) ? list : list.data || []);
      setMeta(data.data ? { current_page: data.current_page, last_page: data.last_page, total: data.total, per_page: data.per_page, per_page_options: data.data.per_page_options } : null);
    } catch (err) {
      showError('Failed to load items', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.inventory.units().then(({ data }) => {
      const list = Array.isArray(data) ? data : data.units || [];
      setUnits(list);
    }).catch(() => {});
  }, []);

  const openCreateItem = () => { setEditing(null); setItemForm(emptyItemForm); setErrors({}); setItemModal(true); };
  const openEditItem = (row) => {
    setEditing(row);
    setItemForm({
      name: row.name, sku: row.sku || '', unit_id: row.unit_id || (row.unit ? row.unit.id : ''),
      opening_qty: row.opening_qty || 0, is_active: row.is_active !== false,
    });
    setErrors({});
    setItemModal(true);
  };

  const saveItem = async () => {
    const errs = {};
    if (!itemForm.name) errs.name = 'Name is required';
    if (!itemForm.sku) errs.sku = 'SKU is required';
    if (!itemForm.unit_id) errs.unit_id = 'Unit is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.inventory.updateItem(editing.id, itemForm);
        toast('success', '', 'Item updated');
      } else {
        await api.inventory.createItem(itemForm);
        toast('success', '', 'Item created');
      }
      setItemModal(false);
      load();
    } catch (err) {
      showError('Failed to save item', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (row) => {
    const res = await confirmDelete(`item "${row.name}"`);
    if (!res.isConfirmed) return;
    try {
      await api.inventory.deleteItem(row.id);
      toast('success', '', 'Item deleted');
      load();
    } catch (err) {
      showError('Failed to delete item', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Inventory Items"
        subtitle="Manage inventory items and SKUs."
        crumbs={[{ label: 'Inventory' }, { label: 'Items' }]}
        actions={hasPermission('create-inventory') && (
          <button className="btn btn-primary" onClick={openCreateItem}><FiPlus /> Add Item</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input placeholder="Search items..." value={params.search} onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })} />
            </div>
          </div>

          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="items" label="inventory items" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No items found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'name', label: 'Item', render: (r) => <b>{r.name}</b> },
              { key: 'sku', label: 'SKU' },
              { key: 'unit', label: 'Unit', render: (r) => (r.unit ? r.unit.short_name || r.unit.name : '-') },
              { key: 'opening_qty', label: 'Opening Qty', align: 'right' },
              { key: 'is_active', label: 'Status', render: (r) => <StatusBadge status={r.is_active ? 'active' : 'inactive'} /> },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {hasPermission('update-inventory') && <button className="btn-icon" onClick={() => openEditItem(r)}><FiEdit2 /></button>}
                  {hasPermission('delete-inventory') && <button className="btn-icon danger" onClick={() => deleteItem(r)}><FiTrash2 /></button>}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>

      <Modal open={itemModal} onClose={() => { setItemModal(false); setErrors({}); }} title={editing ? 'Edit Item' : 'Add Item'}
        footer={<>
          <button className="btn btn-secondary" onClick={() => { setItemModal(false); setErrors({}); }}>Cancel</button>
          <button className="btn btn-primary" onClick={saveItem} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </>}
      >
        <div className="grid-2">
          <div className="form-group"><label className="form-label">Name <span className="req">*</span></label>
            <input className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={itemForm.name} onChange={(e) => { setItemForm({ ...itemForm, name: e.target.value }); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }} />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}</div>
          <div className="form-group"><label className="form-label">SKU <span className="req">*</span></label>
            <input className={`form-control ${errors.sku ? 'is-invalid' : ''}`} value={itemForm.sku} onChange={(e) => { setItemForm({ ...itemForm, sku: e.target.value }); if (errors.sku) setErrors((p) => ({ ...p, sku: '' })); }} />
            {errors.sku && <div className="invalid-feedback">{errors.sku}</div>}</div>
          <div className="form-group"><label className="form-label">Unit <span className="req">*</span></label>
            <SearchableSelect options={units} value={itemForm.unit_id} onChange={(v) => { setItemForm({ ...itemForm, unit_id: v }); if (errors.unit_id) setErrors((p) => ({ ...p, unit_id: '' })); }} getLabel={(u) => (u.short_name ? u.name + ' (' + u.short_name + ')' : u.name)} isInvalid={!!errors.unit_id} placeholder="-- Select unit --" />
            {errors.unit_id && <div className="invalid-feedback">{errors.unit_id}</div>}</div>
          {!editing && <div className="form-group"><label className="form-label">Opening Qty</label>
            <input className="form-control" type="number" value={itemForm.opening_qty} onChange={(e) => setItemForm({ ...itemForm, opening_qty: e.target.value })} /></div>}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
          <input type="checkbox" checked={itemForm.is_active} onChange={(e) => setItemForm({ ...itemForm, is_active: e.target.checked })} /> Active item
        </label>
      </Modal>
    </div>
  );
}
